import { z } from 'zod'
import { slugifyTag } from './tags.ts'

/**
 * Notes and note categories.
 *
 * A category's `requiredPermission` is the whole visibility model: hold it and
 * you can read and file notes in that category, don't and the notes may as
 * well not exist (the API omits them, it does not return a redacted stub — a
 * "3 hidden notes" badge tells the office who is in counseling).
 */

export const NOTE_VISIBILITIES = ['people:read', 'people:write', 'people:admin'] as const
export type NoteVisibility = (typeof NOTE_VISIBILITIES)[number]

/** Labels for the category editor; the permission string is the source of truth. */
export const NOTE_VISIBILITY_LABELS: Record<NoteVisibility, string> = {
  'people:read': 'Anyone who can see people',
  'people:write': 'Staff who can edit people',
  'people:admin': 'People admins only',
}

export const slugifyNoteCategory = slugifyTag

export const createNoteCategorySchema = z.object({
  name: z.string().trim().min(1, 'Name the category').max(60),
  requiredPermission: z.enum(NOTE_VISIBILITIES).default('people:read'),
})
export type CreateNoteCategoryInput = z.infer<typeof createNoteCategorySchema>
export type CreateNoteCategoryFormInput = z.input<typeof createNoteCategorySchema>

export const updateNoteCategorySchema = createNoteCategorySchema.partial()
export type UpdateNoteCategoryInput = z.infer<typeof updateNoteCategorySchema>

const noteBody = z.string().trim().min(1, 'Write something').max(10_000)

export const createNoteSchema = z.object({
  personId: z.uuid(),
  /** Omitted ⇒ the org's default category. */
  categoryId: z.uuid().nullish(),
  body: noteBody,
  pinned: z.boolean().optional(),
})
export type CreateNoteInput = z.infer<typeof createNoteSchema>
export type CreateNoteFormInput = z.input<typeof createNoteSchema>

export const updateNoteSchema = z.object({
  body: noteBody.optional(),
  categoryId: z.uuid().optional(),
  pinned: z.boolean().optional(),
})
export type UpdateNoteInput = z.infer<typeof updateNoteSchema>
export type UpdateNoteFormInput = z.input<typeof updateNoteSchema>
